
import Numpad from '@/libs/common/components/Numpad.vue';
import { Component, Prop, VModel, Watch } from 'vue-property-decorator';
import Page from '@/Page.vue';
import KeyInput from '@/libs/common/components/KeyInput.vue';
import ApiService from '@/libs/core/api-service';
import { HttpResponse, TenantModel, TokenErrorResponseApiResultFailed, TokenResponseApiResultSuccess } from '@/libs/Api';
import { setAccessToken } from 'axios-jwt';
import { authStore } from '@/libs/auth/+store/store';
import { AuthAction, AuthGetter } from '@/libs/auth/models/auth-state';
import { EventTypeEnum } from '@/libs/core/models/event-type-enum';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import QuickLoginPanelDashboardDialog from '@/libs/common/components-smart/QuickLoginPanelDashboardDialog.vue';
import { validate as isValidUUID } from 'uuid';

enum NumpadTypeEnum {
    PersonalNumber = 'PersonalNumber',
    Pin = 'Pin',
}

@Component({
    components: { QuickLoginPanelDashboardDialog, KeyInput, Numpad },
})
export default class QuickLoginDialog extends Page {
    @VModel() dialog!: boolean;
    @Prop() locked!: boolean;
    protected personalNumber = '';
    protected pin = '';
    protected numpadValue = '';
    protected numpadMinLength = 1;
    protected numpadType = NumpadTypeEnum.PersonalNumber;
    protected NumpadTypeEnum: typeof NumpadTypeEnum = NumpadTypeEnum;

    protected isLoggingIn = false;
    protected loginError = '';

    protected isValidated = false;

    protected lcdDialog = false;
    protected interactionTimeout: any = null;

    @Watch('locked')
    lockedChanged(v: boolean) {
        if (v) {
            this.showLcdPanel();
        }
    }

    @Watch('lcdDialog')
    lcdDialogChanged(v: boolean) {
        console.log('lcdDialog', v);
    }

    mounted() {
        if (this.locked) {
            this.showLcdPanel();
        }
    }

    public isValidGUID(value: string): boolean {
        let regex = new RegExp(/^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/);

        // if str
        // is empty return false
        if (value == null) {
            return false;
        }

        // Return true if the str
        // matched the ReGex
        if (regex.test(value) == true) {
            return true;
        } else {
            return false;
        }
    }

    public hasValidActivePanelDashboard(): boolean {
        if (!window.localStorage.ActivePanelDashboard) {
            return false;
        }
        if (window.localStorage.ActivePanelDashboard.length == 0) {
            return false;
        }
        try {
            if (!this.isValidGUID(window.localStorage.ActivePanelDashboard ?? '')) {
                return false;
            }
        } catch (ex) {
            return false;
        }
        return true;
    }

    public setPersonalNumber(personalNumber: string) {
        this.reset();
        this.personalNumber = personalNumber;
        this.numpadType = NumpadTypeEnum.Pin;
    }

    protected get personalNumberPositionCount(): number {
        const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
        return Math.min(10, Math.max(1, tenant.quickLoginPersonalNumberPositionCount ?? 6));
    }

    protected get numpadMaxLength(): number {
        if (this.numpadType == NumpadTypeEnum.PersonalNumber) {
            const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
            return Math.min(10, Math.max(1, tenant.quickLoginPersonalNumberPositionCount ?? 6));
        } else {
            return 4;
        }
    }

    protected onQuickLoginCloseClick(closeTake = true) {
        this.reset();
        if (closeTake) {
            this.setQuery('take', null);
        }
        this.$emit('input', false);
    }

    protected onActivatePersonalNumberClick() {
        this.numpadValue = this.personalNumber;
        this.numpadMinLength = 1;
        this.numpadType = NumpadTypeEnum.PersonalNumber;
    }

    protected onActivatePinClick() {
        this.numpadValue = this.pin;
        this.numpadMinLength = 4;
        this.numpadType = NumpadTypeEnum.Pin;
    }

    protected onNumpadValueChange() {
        this.loginError = '';
        switch (this.numpadType) {
            case NumpadTypeEnum.PersonalNumber:
                this.personalNumber = this.numpadValue;
                break;
            case NumpadTypeEnum.Pin:
                this.pin = this.numpadValue;
                break;
        }
    }

    protected onSubmitClick() {
        switch (this.numpadType) {
            case NumpadTypeEnum.PersonalNumber:
                this.pin = '';
                this.onActivatePinClick();
                break;
            case NumpadTypeEnum.Pin:
                this.onMakeLoginClick();
                break;
        }
    }

    protected filled() {
        if (this.numpadType == NumpadTypeEnum.PersonalNumber) {
            this.pin = '';
            this.onActivatePinClick();
        }
        if (this.numpadType == NumpadTypeEnum.Pin) {
            if (this.pin.length == 4 && this.personalNumber.length > 0) {
                this.onMakeLoginClick(false);
            }
        }
    }

    protected onMakeLoginClick(clearOnError = true) {
        this.isLoggingIn = true;
        this.loginError = '';
        ApiService.api
            .makeQuickLoginPin({
                employeeId: this.personalNumber,
                pin: this.pin,
            })
            .then((e: HttpResponse<TokenResponseApiResultSuccess>) => {
                this.isLoggingIn = false;
                if (e.data.returnValue && e.data.returnValue.accessToken && e.data.returnValue.accessToken.length > 0) {
                    this.isValidated = true;
                    setAccessToken(e.data.returnValue!.accessToken);
                    authStore.dispatch(AuthAction.UserInfo);
                    (window as any).Journaly.publish(EventTypeEnum.SignedIn);
                    applicationUserStore.dispatch(CrudAction.Get, {});
                    this.subscribe(applicationUserStore, CrudReponse.Get).then((e: any) => {
                        tenantsStore.dispatch(CrudAction.GetAll, createCrudQueryPayload());
                        this.subscribe(tenantsStore, CrudReponse.GetAll)
                            .then((e: TenantModel[]) => {
                                if (
                                    window.localStorage.CurrentTenant &&
                                    e.any((x) => x.id == window.localStorage.CurrentTenant)
                                ) {
                                    authStore.dispatch(AuthAction.SetTenant, window.localStorage.CurrentTenant);
                                } else if (e.length > 0) {
                                    authStore.dispatch(AuthAction.SetTenant, e[0].id!);
                                    this.$router.push({ name: 'Home', params: { tenantId: e[0].id! } });
                                } else {
                                    this.$router.push({ name: 'UserSettings' });
                                }
                                window.localStorage.QuickLoginLocked = null;
                                this.$emit('unlocked');
                                this.onQuickLoginCloseClick(false);
                            })
                            .catch(() => {
                                this.$router.push({ name: 'UserSettings' });
                            });
                    });
                } else {
                    this.loginError = 'Unknown';
                }
            })
            .catch((e: HttpResponse<TokenResponseApiResultSuccess, TokenErrorResponseApiResultFailed>) => {
                this.isLoggingIn = false;
                this.loginError = e.error.returnValue?.errorDescription ?? 'Unknown';
                if (clearOnError) {
                    setTimeout(() => {
                        this.reset(false);
                    }, 500);
                } else {
                    this.pin = '';
                    this.numpadValue = '';
                }
            });
    }

    protected reset(resetError = true) {
        this.personalNumber = '';
        this.pin = '';
        this.numpadValue = '';
        this.numpadMinLength = 1;
        this.numpadType = NumpadTypeEnum.PersonalNumber;
        this.isLoggingIn = false;
        if (resetError) {
            this.loginError = '';
        }
        this.isValidated = false;
    }

    protected onDeactivateClick() {
        this.reset();
        this.$emit('deactivate');
        this.onQuickLoginCloseClick();
    }

    private showLcdPanel() {
        if (window.localStorage.ActivePanelDashboard) {
            this.activateInteractionListeners();
            this.startInteractionTimeout();
        }
    }

    private onInteraction() {
        this.lcdDialog = false;
        this.clearInteractionListeners();
        this.startInteractionTimeout();
    }

    private startInteractionTimeout() {
        clearTimeout(this.interactionTimeout);
        this.interactionTimeout = setTimeout(() => {
            this.lcdDialog = true;
            this.activateInteractionListeners();
        }, 60000);
    }

    private clearInteractionListeners() {
        document.removeEventListener('click', this.onInteraction);
        document.removeEventListener('keydown', this.onInteraction);
        document.removeEventListener('touchstart', this.onInteraction);
        document.removeEventListener('scroll', this.onInteraction);
    }

    private activateInteractionListeners() {
        document.addEventListener('click', this.onInteraction);
        document.addEventListener('keydown', this.onInteraction);
        document.addEventListener('touchstart', this.onInteraction);
        document.addEventListener('scroll', this.onInteraction);
    }
}
